import * as React from "react";
import {connect} from "react-redux";
import {RouteComponentProps} from "react-router";
import * as validator from "validator";
import {none, Option, option} from "ts-option";
import AddressSelectionComponent from "../address/select/AddressSelectionComponent";
import Address from "../../model/address/Address";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import Input from "../../hiberUiKit/src/atoms/Input/Input";
import {RootState} from "../../store/reducer/root/RootState";
import SelfServiceSignupApi, {
	selfServiceSignupApi,
	SelfServiceSignupAddressApiDto,
	SelfServiceSignupApiDto
} from "../../api/SelfServiceSignupApi";
import TurnstileWidget from "./TurnstileWidget";
import "./selfServiceSignupPage.scss";

const logo = require("../../../resources/hiber/img/dg-logo-big-green.png");

interface ReduxProps {
	turnstileSiteKey: string;
}

/**
 * Address typed by hand, used when the picker cannot find the driver's address - a rural address, or the
 * lookup provider being unavailable. Street, city and Eircode are required, the rest is optional.
 */
interface ManualAddress {
	street: string;
	city: string;
	eircode: string;
	buildingName: string;
	flatName: string;
	buildingNumber: string;
}

const emptyManualAddress: ManualAddress = {
	street: "",
	city: "",
	eircode: "",
	buildingName: "",
	flatName: "",
	buildingNumber: ""
};

interface State {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	businessName: string;
	address: Option<Address>;
	manualEntry: boolean;
	manualAddress: ManualAddress;
	turnstileToken: string;
	loading: boolean;
	success: boolean;
	errors: {[field: string]: string};
	requestError: string;
}

type Props = ReduxProps & RouteComponentProps<{}>;

export class SelfServiceSignupPage extends React.Component<Props, State> {
	private signupApi: SelfServiceSignupApi = selfServiceSignupApi;
	private addressSelection: AddressSelectionComponent;
	private turnstileWidget: TurnstileWidget;

	constructor(props: Props) {
		super(props);
		this.state = {
			firstName: "",
			lastName: "",
			email: "",
			phoneNumber: "",
			businessName: "",
			address: none,
			manualEntry: false,
			manualAddress: emptyManualAddress,
			turnstileToken: "",
			loading: false,
			success: false,
			errors: {},
			requestError: ""
		};
		this.submit = this.submit.bind(this);
	}

	render() {
		return (
			<div className="c-self-service-signup">
				<div className="c-self-service-signup_card">
					<img src={logo} className="c-self-service-signup_logo"/>
					{this.state.success ? this.renderSuccess() : this.renderForm()}
				</div>
			</div>
		);
	}

	private renderForm() {
		return (
			<form onSubmit={this.submit} noValidate={true}>
				<h1>Create your Driving Green account</h1>
				<p className="c-self-service-signup_intro">
					Register your business. We will email you a secure link to set your password.
				</p>

				<div className="c-self-service-signup_grid">
					{this.renderInput("firstName", "First name", "text", 100)}
					{this.renderInput("lastName", "Last name", "text", 100)}
				</div>
				{this.renderInput("email", "Email address", "email", 254)}
				{this.renderInput("phoneNumber", "Phone number", "text", 32, "+353 87 123 4567")}
				{this.renderInput("businessName", "Business name", "text", 200)}

				<div className="c-self-service-signup_field c-self-service-signup_address">
					<label>Business address</label>
					{this.renderAddress()}
					{this.renderFieldError("address")}
				</div>

				<div className="c-self-service-signup_turnstile">
					{this.props.turnstileSiteKey ? (
						<TurnstileWidget
							ref={(component) => this.turnstileWidget = component}
							siteKey={this.props.turnstileSiteKey}
							onTokenChange={(turnstileToken) => this.setState({turnstileToken})}
							onError={() => this.setState({requestError: "Security check could not be loaded. Please refresh and try again."})}/>
					) : (
						<div className="c-self-service-signup_config-error">
							Registration is not configured for this environment.
						</div>
					)}
					{this.renderFieldError("turnstileToken")}
				</div>

				{this.state.requestError && <div className="c-self-service-signup_request-error" role="alert">
					{this.state.requestError}
				</div>}

				<div className="c-self-service-signup_actions">
					<ButtonPrimary label="Create account"
								   onClick={(event) => this.submit(event)}
								   isLoading={this.state.loading}
								   disabled={this.state.loading || !this.props.turnstileSiteKey}/>
				</div>
			</form>
		);
	}

	private renderAddress() {
		if (this.state.manualEntry) {
			return (
				<div className="c-self-service-signup_manual-address">
					{this.renderManualInput("street", "Street name (optional)", 100)}
					{this.renderManualInput("city", "City or town", 100)}
					{this.renderManualInput("eircode", "Eircode", 16)}
					{this.renderManualInput("buildingName", "Building name (optional)", 100)}
					{this.renderManualInput("flatName", "Flat name (optional)", 100)}
					{this.renderManualInput("buildingNumber", "Building number (optional)", 32)}
					<button type="button"
							className="c-self-service-signup_address-toggle"
							onClick={() => this.useAddressSearch()}>
						Search for the address instead
					</button>
				</div>
			);
		}
		return this.state.address.map(address => (
			<div className="c-self-service-signup_selected-address">
				<span>{address.displayName()}</span>
				<button type="button" onClick={() => this.clearAddress()}>Change</button>
			</div>
		)).getOrElse(() => (
			<div>
				<AddressSelectionComponent
					ref={(component) => this.addressSelection = component}
					addressApi={this.signupApi.addressApi}
					onSelect={(address) => this.selectAddress(address)}
					visible={true}
					placeholder="Start typing an Eircode or address"
					initialSearchQuery={none}/>
				<button type="button"
						className="c-self-service-signup_address-toggle"
						onClick={() => this.useManualEntry()}>
					Can't find your address? Enter it manually
				</button>
			</div>
		));
	}

	private renderManualInput(field: keyof ManualAddress, label: string, maxLength: number) {
		return (
			<div className="c-self-service-signup_field">
				<label htmlFor={`signup-address-${field}`}>{label}</label>
				<Input type="text"
					   id={`signup-address-${field}`}
					   value={this.state.manualAddress[field]}
					   onChange={(event) => this.updateManualField(field, event.target.value.substring(0, maxLength))}
					   placeholder={label}/>
				{this.renderFieldError(this.manualErrorField(field))}
			</div>
		);
	}

	private renderSuccess() {
		return (
			<div className="c-self-service-signup_success" role="status">
				<div className="c-self-service-signup_success-icon">✓</div>
				<h1>Check your email</h1>
				<p>
					Your account has been created. Use the secure link we sent to <strong>{this.state.email}</strong> to
					 set your password, then sign in to the Driving Green mobile app.
				</p>
			</div>
		);
	}

	private renderInput(field: "firstName" | "lastName" | "email" | "phoneNumber" | "businessName",
		label: string,
		type: "text" | "email",
		maxLength: number,
		placeholder?: string) {
		return (
			<div className="c-self-service-signup_field">
				<label htmlFor={`signup-${field}`}>{label}</label>
				<Input type={type}
					   id={`signup-${field}`}
					   value={(this.state as any)[field]}
					   onChange={(event) => this.updateField(field, event.target.value.substring(0, maxLength))}
					   placeholder={placeholder || label}/>
				{this.renderFieldError(field)}
			</div>
		);
	}

	private renderFieldError(field: string) {
		return this.state.errors[field]
			? <span className="c-self-service-signup_field-error">{this.state.errors[field]}</span>
			: null;
	}

	private updateField(field: string, value: string) {
		const nextState: any = {requestError: ""};
		nextState[field] = value;
		const errors = {...this.state.errors};
		delete errors[field];
		nextState.errors = errors;
		this.setState(nextState);
	}

	private selectAddress(address: Address) {
		const errors = {...this.state.errors};
		delete errors.address;
		this.setState({address: option(address), errors, requestError: ""});
	}

	private manualErrorField(field: keyof ManualAddress): string {
		return `manualAddress.${field}`;
	}

	private updateManualField(field: keyof ManualAddress, value: string) {
		const manualAddress: ManualAddress = {...this.state.manualAddress};
		manualAddress[field] = value;
		const errors = {...this.state.errors};
		delete errors[this.manualErrorField(field)];
		this.setState({manualAddress, errors, requestError: ""});
	}

	private useManualEntry() {
		const errors = {...this.state.errors};
		delete errors.address;
		this.setState({manualEntry: true, address: none, errors, requestError: ""});
	}

	private useAddressSearch() {
		const errors = {...this.state.errors};
		Object.keys(errors)
			.filter(key => key.indexOf("manualAddress.") === 0)
			.forEach(key => delete errors[key]);
		this.setState({manualEntry: false, manualAddress: emptyManualAddress, errors, requestError: ""});
	}

	private clearAddress() {
		// Picking a different address is how the driver fixes a rejected one, so the complaint about the old
		// address has to go with it.
		const errors = {...this.state.errors};
		delete errors.address;
		this.setState({address: none, errors, requestError: ""}, () => {
			if (this.addressSelection) {
				this.addressSelection.resetSearchInput();
			}
		});
	}

	private submit(event?: React.SyntheticEvent<any>) {
		if (event) {
			event.preventDefault();
		}
		if (this.state.loading) {
			return;
		}

		const errors = this.validate();
		if (Object.keys(errors).length) {
			this.setState({errors, requestError: ""});
			return;
		}

		this.setState({loading: true, errors: {}, requestError: ""});
		this.signupApi.signUp(this.toApiData()).then(() => {
			this.setState({loading: false, success: true});
		}).catch((error) => {
			if (this.turnstileWidget) {
				this.turnstileWidget.reset();
			}
			this.setState({loading: false, requestError: this.errorMessage(error)});
		});
	}

	private validate(): {[field: string]: string} {
		const errors: {[field: string]: string} = {};
		if (!this.state.firstName.trim()) errors.firstName = "First name is required";
		if (!this.state.lastName.trim()) errors.lastName = "Last name is required";
		if (!this.state.email.trim()) errors.email = "Email address is required";
		else if (!validator.isEmail(this.state.email.trim())) errors.email = "Enter a valid email address";
		if (!this.state.phoneNumber.trim()) errors.phoneNumber = "Phone number is required";
		if (!this.state.businessName.trim()) errors.businessName = "Business name is required";
		if (this.state.manualEntry) {
			const manual = this.state.manualAddress;
			if (!manual.city.trim()) errors["manualAddress.city"] = "City or town is required";
			if (!manual.eircode.trim()) errors["manualAddress.eircode"] = "Eircode is required";
		} else if (this.state.address.isEmpty) {
			errors.address = "Select your business address";
		} else {
			const address = this.state.address.get;
			// The street is not required: plenty of Irish addresses have none, and the Eircode identifies
			// the address on its own.
			if (address.city.isEmpty || !address.postcode.trim()) {
				errors.address = "Select an address with a city and Eircode";
			}
		}
		if (!this.state.turnstileToken) errors.turnstileToken = "Complete the security check";
		return errors;
	}

	private toApiData(): SelfServiceSignupApiDto {
		return {
			firstName: this.state.firstName.trim(),
			lastName: this.state.lastName.trim(),
			email: this.state.email.trim(),
			phoneNumber: this.state.phoneNumber.trim(),
			businessName: this.state.businessName.trim(),
			address: this.addressApiData(),
			turnstileToken: this.state.turnstileToken,
			jobTypes: ["SERVICE"]
		};
	}

	private addressApiData(): SelfServiceSignupAddressApiDto {
		if (this.state.manualEntry) {
			const manual = this.state.manualAddress;
			return {
				street: manual.street.trim(),
				city: manual.city.trim(),
				eircode: manual.eircode.trim(),
				buildingName: this.orNull(manual.buildingName),
				buildingNumber: this.orNull(manual.buildingNumber),
				// "Flat name" in the client's document is the sub building on our address model.
				subBuilding: this.orNull(manual.flatName)
			};
		}
		const address = this.state.address.get;
		return {
			street: address.street.orNull,
			city: address.city.get,
			eircode: address.postcode,
			buildingName: address.buildingName.orNull,
			buildingNumber: address.buildingNumber.orNull,
			subBuilding: address.subBuilding.orNull,
			secStreet: address.secondaryStreet.orNull,
			district: address.district.orNull,
			province: address.province.orNull
		};
	}

	private orNull(value: string): string | null {
		return value.trim().length ? value.trim() : null;
	}

	private errorMessage(error: any): string {
		const response = error && error.response;
		const status = response && response.status;
		const exception = response && response.data && response.data.exception;
		const message = response && response.data && response.data.message;
		switch (exception) {
			case "EmailExistsException":
				return "An account already exists for this email address. Try signing in or reset your password.";
			// A name already in use no longer refuses a registration - the back end registers a numbered
			// variant of it. This is left for the one case it cannot: two registrations submitted in the
			// same instant, where submitting again is all that is needed.
			case "DuplicatedBrandNameException":
				return "We could not register that business name just now. Please try again.";
			case "BotCheckFailedException":
				if (message === "Bot check is unavailable") {
					return "The security check is temporarily unavailable. Please try again later.";
				}
				return "The security check was not accepted. Please complete it again.";
			case "TooManyRequestsException":
				return "Too many registration attempts. Please try again later.";
			case "SignupNotConfiguredException":
				return "Registration is temporarily unavailable. Please try again later.";
		}
		if (status === 400) return "Please check the information in the form and try again.";
		if (status === 403) return "The security check was not accepted. Please complete it again.";
		if (status === 409) return "This email address or business name is already registered.";
		if (status === 429) return "Too many registration attempts. Please try again later.";
		if (status === 503) return "Registration is temporarily unavailable. Please try again later.";
		return "We could not create your account. Please try again.";
	}
}

function mapStateToProps(state: RootState): ReduxProps {
	return {
		turnstileSiteKey: state.settings.settings.map(settings => settings.turnstileSiteKey).getOrElse("")
	};
}

export default connect(mapStateToProps)(SelfServiceSignupPage);
